import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Icon, Text } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import type { DragAndDropHandlerProps } from './DragAndDropHandler.types';
import { SUPPORTED_ALL_FORMATS } from '../../utils/fileUploadConfig';

export const DragAndDropHandler: React.FC<DragAndDropHandlerProps> = ({
  children,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onDrop,
  disabled = false,
  onDragStateChange,
  className = '',
  useDragCounter = false,
  showOverlay = false,
  overlayContent,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled) {
      return;
    }

    if (useDragCounter) {
      setDragCounter(prev => prev + 1);
    }

    setIsDragOver(true);
    onDragStateChange?.(true);

    onDragEnter?.(e);
  }, [disabled, useDragCounter, onDragEnter, onDragStateChange]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled) {
      return;
    }

    onDragOver?.(e);
  }, [disabled, onDragOver]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled) {
      return;
    }

    if (useDragCounter) {
      setDragCounter(prev => {
        const newCounter = prev - 1;
        if (newCounter <= 0) {
          setIsDragOver(false);
          onDragStateChange?.(false);
          return 0;
        }
        return newCounter;
      });
    } else {
      // Check if we're actually leaving the drop zone by comparing relatedTarget
      // If relatedTarget is null or not a child of currentTarget, we're leaving
      const relatedTarget = e.relatedTarget as Node | null;
      const currentTarget = e.currentTarget as Node;
      
      if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
        setIsDragOver(false);
        onDragStateChange?.(false);
      }
    }

    onDragLeave?.(e);
  }, [disabled, useDragCounter, onDragLeave, onDragStateChange]);

  // Add global dragend listener to reset state when drag operation ends
  useEffect(() => {
    const handleGlobalDragEnd = () => {
      setIsDragOver(false);
      onDragStateChange?.(false);
      setDragCounter(0);
    };

    document.addEventListener('dragend', handleGlobalDragEnd);
    return () => {
      document.removeEventListener('dragend', handleGlobalDragEnd);
    };
  }, [onDragStateChange]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragOver(false);
    onDragStateChange?.(false);
    setDragCounter(0);

    if (disabled) {
      return;
    }

    onDrop?.(e);
  }, [disabled, onDrop, onDragStateChange]);

  // Handle dragend to reset state when drag operation ends (e.g., user drags outside window or presses Escape)
  const handleDragEnd = useCallback((e: React.DragEvent) => {
    setIsDragOver(false);
    onDragStateChange?.(false);
    setDragCounter(0);
  }, [onDragStateChange]);

  return (
    <div
      ref={containerRef}
      className={`relative ${className} ${isDragOver && showOverlay && !disabled ? 'border-2 border-dashed border-utility-brand-600' : ''}`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onDragEnd={handleDragEnd}
    >
      {children}
      
      {/* Overlay covering the whole container */}
      {isDragOver && showOverlay && !disabled && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-bg-primary backdrop-blur-sm pointer-events-none">
          {overlayContent || (
            <div className="flex flex-col items-center gap-bg-v2-lg text-center px-bg-v2-xl py-bg-v2-2xl bg-bg-primary border-2 border-dashed border-utility-brand-600 rounded-bg-v2-lg container-shadow-lg">
              <div className="w-16 h-16 flex items-center justify-center bg-utility-brand-50 rounded-full">
                <Icon name={IconName.Upload} size="xl" className="text-utility-brand-600" />
              </div>
              <div className="flex flex-col gap-bg-v2-xs">
                <Text variant="text-lg" weight="semibold" color="primary">
                  Drop files here to upload
                </Text>
                <Text variant="text-sm" color="tertiary">
                  Supported formats: {SUPPORTED_ALL_FORMATS}
                </Text>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

